import {
    Component,
    ViewEncapsulation,
    TemplateRef,
    ContentChild,
    Input,
    OnInit,
    inject
} from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { useTranslationNamespace, LgTranslateService } from "@logex/framework/lg-localization";
import {
    IAppVersion,
    LG_APP_VERSION,
    LG_USER_INFO,
    IUser,
    VersionService,
    IVersion,
    LG_APP_INFO,
    LG_USERFLOW_SERVICE,
    LG_APP_CONFIGURATION
} from "@logex/framework/lg-application";

import { LgReleaseNotesDialogFactory } from "../../lg-release-notes/lg-release-notes-dialog.component";
import { ISidebar } from "../sidebar-context";

export interface SidebarHelpContext {
    $implicit: IVersion;
    appVersion: IAppVersion;
    user: IUser;
    internal: boolean;
}

export interface SidebarHelpStandardLinks {
    manual?: string;
    FAQ?: string;
    dataSpecification?: string;
    webinar?: string;
    serviceDesk?: string;
}

export interface SidebarHelpLinkItem {
    name: string;
    url: string;
    icon?: string;
}

enum StandardSidebarLinksIcon {
    manual = "icon-manual",
    FAQ = "icon-faq",
    dataSpecification = "icon-regel",
    webinar = "icon-webinar",
    serviceDesk = "icon-helpcenter"
}

enum StandardSidebarLinksPosition {
    manual = 1,
    FAQ = 2,
    dataSpecification = 3,
    webinar = 4,
    serviceDesk = 5
}

// ----------------------------------------------------------------------------------
//
@Component({
    selector: "lg-sidebar-help",
    templateUrl: "./lg-sidebar-help.component.html",
    host: {
        class: "lg-sidebar-help flexcol"
    },
    encapsulation: ViewEncapsulation.None,
    viewProviders: [useTranslationNamespace("FW._Sidebar._Help")]
})
export class LgSidebarHelpComponent implements OnInit {
    appConfig = inject(LG_APP_CONFIGURATION);
    appInfo = inject(LG_APP_INFO);
    appVersion = inject(LG_APP_VERSION);
    userflowService = inject(LG_USERFLOW_SERVICE);
    userInfo = inject(LG_USER_INFO);
    private _releaseNotes = inject(LgReleaseNotesDialogFactory);
    private _translationService = inject(LgTranslateService);
    private _versionService = inject<VersionService<IVersion>>(VersionService<IVersion>);

    /**
     * Specifies the link to the support page of desired product.
     * Defaults to "https://support.logex.nl/".
     */
    @Input() supportLink = "https://support.logex.nl/";
    /**
     * Specifies standard list of links. If null, the item is not shown in the list.
     * There are four standard links.
     *
     * @type { SidebarHelpStandardLinks }
     * @property { string } manual - Specifies the url to manual.
     * @property { string } FAQ - Specifies the url to frequently asked questions.
     * @property { string } dataSpecification - Specifies the url to data specifications.
     * @property { string } webinar - Specifies the url to webinar.
     */
    @Input() standardLinks: SidebarHelpStandardLinks | null;
    /**
     * Specifies complementary items to the standard help links.
     * These links are shown in the order passed to the component.
     * Defaults to [].
     *
     * @type { Array<SidebarHelpLinkItem> }
     * @property { string } name - Specifies localized name to show in the sidebar.
     * @property { string } url - Specifies the url to the desired page.
     * @property { string } icon - Optional parameter. Specifies the icon shown before the name.
     */
    @Input() additionalItems: SidebarHelpLinkItem[] = [];
    /**
     * Specifies whether release notes are clickable or not
     */
    @Input() versionClickable = true;
    /**
     * If specified, the sidebar is closed on checklist click
     */
    @Input() sidebar: ISidebar | undefined;

    _internal: boolean;
    _displayVersion: number | string;
    _context: SidebarHelpContext;
    _version: IVersion;
    _isChecklistReady = false;
    _standardLinks: SidebarHelpLinkItem[];

    readonly isChecklistReady$: Observable<boolean>;
    @ContentChild("version") _versionTemplateRef: TemplateRef<SidebarHelpContext>;

    @ContentChild("extension") _extensionTemplateRef: TemplateRef<SidebarHelpContext>;

    constructor() {
        this._internal = this.userInfo.hasPermission("internal");

        this._context = {
            $implicit: null,
            appVersion: this.appVersion,
            user: this.userInfo,
            internal: this._internal
        };

        this._versionService.version.subscribe(version => {
            this._context.$implicit = version;
            this._version = version;
            this._displayVersion =
                version && version.display_version_id != null
                    ? version.display_version_id
                    : "Version unavailable";
        });

        this.isChecklistReady$ = this.userflowService.ready$.pipe(
            map(() => !!this.appConfig.userflowContentId)
        );
    }

    ngOnInit(): void {
        if (this.standardLinks == null) return;
        this._standardLinks = this._getMappedStandardLinks();
    }

    private _getMappedStandardLinks(): SidebarHelpLinkItem[] {
        const result: SidebarHelpLinkItem[] = Object.keys(this.standardLinks)
            .map(x => {
                const localizationId = x.charAt(0).toUpperCase() + x.slice(1);
                return <SidebarHelpLinkItem>{
                    url: this.standardLinks[x as keyof SidebarHelpStandardLinks],
                    name: this._translationService.translate("." + localizationId),
                    icon: StandardSidebarLinksIcon[x as keyof typeof StandardSidebarLinksIcon],
                    sort: StandardSidebarLinksPosition[
                        x as keyof typeof StandardSidebarLinksPosition
                    ]
                };
            })
            .sort((x, y) => (x as any).sort - (y as any).sort);
        return result;
    }

    _showReleaseNotes(): void {
        if (!this.versionClickable) return;
        this._releaseNotes.show();
    }

    _openSupportPage(): void {
        window.open(this.supportLink, "_blank");
    }

    async showChecklist($event: Event): Promise<void> {
        $event.preventDefault();
        $event.stopPropagation();

        this.userflowService.showContent(this.appConfig.userflowContentId);
        this.sidebar?.dismissPanel();
    }
}
